import { createReducer, on } from '@ngrx/store';
import * as UserGroupActions from '../actions/employee-group.actions';
import * as UserActions from '../actions/employee.actions';
import { EmployeeGroup } from '@fc-core';
import { buildGroupUpdatesForEmployeeGroups } from './employee-group-membership.utils';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { changeSortByListOrder } from '@fc-shared/utils/change-sort-by-list-order';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';

export const employeeGroupFeatureKey = 'employeeGroups';

export interface State extends EntityState<EmployeeGroup> {
  loading: boolean;
  error: any;
  searchText: string;
  sortByList: SortBy[];
  sortBy: SortBy;
}

export const adapter: EntityAdapter<EmployeeGroup> =
  createEntityAdapter<EmployeeGroup>();

export const initialState: State = adapter.getInitialState({
  loading: false,
  error: null,
  searchText: '',
  sortByList: [
    { name: 'Name', value: 'name', order: false },
    { name: 'Number of users', value: 'numbers', order: null },
  ],
  sortBy: { name: 'Name', value: 'name', order: false },
});
export const reducer = createReducer(
  initialState,
  on(
    UserGroupActions.loadEmployeeGroups,
    UserGroupActions.createUserGroup,
    UserGroupActions.deleteUserGroup,
    UserGroupActions.updateUserGroup,
    (state) => ({ ...state, loading: true }),
  ),
  on(UserGroupActions.resetEmployeeGroupFilters, (state) => ({
    ...state,
    searchText: initialState.searchText,
    sortByList: initialState.sortByList,
    sortBy: initialState.sortBy,
  })),
  on(UserGroupActions.loadEmployeeGroupsSuccess, (state, action) => ({
    ...adapter.setAll(action.userGroups, state),
    loading: false,
  })),
  on(UserGroupActions.createUserGroupSuccess, (state, action) => ({
    ...adapter.addOne(action.userGroup, state),
    loading: false,
  })),
  on(UserGroupActions.deleteUserGroupSuccess, (state, action) => ({
    ...adapter.removeOne(action.groupId, state),
    loading: false,
  })),
  on(UserGroupActions.updateUserGroupSuccess, (state, action) => ({
    ...adapter.updateOne(
      { changes: action.userGroup, id: action.userGroup.id },
      state,
    ),
    loading: false,
  })),
  on(UserGroupActions.employeeGroupsFailure, (state, action) => ({
    ...state,
    error: action.error,
    loading: false,
  })),
  on(UserGroupActions.searchUserGroup, (state, action) => ({
    ...state,
    searchText: action.searchText,
    loading: false,
  })),
  on(UserGroupActions.setUserGroupSortBy, (state, action) => ({
    ...state,
    sortBy: {
      ...action.sortBy,
      order: action.sortBy === null ? false : !action.sortBy.order,
    },
    sortByList: changeSortByListOrder(state.sortByList, action.sortBy),
  })),
  on(UserGroupActions.addEmployeesToGroupSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: { employees: action.group.employees },
        id: action.group.id,
      },
      state,
    ),
  })),
  on(
    UserActions.addEmployeeSuccess,
    UserActions.updateEmployeeSuccess,
    (state, { employee }) =>
      adapter.updateMany(
        buildGroupUpdatesForEmployeeGroups(
          selectAll(state),
          employee.id,
          employee.groups,
        ),
        state,
      ),
  ),
  on(UserGroupActions.removeEmployeesFromGroupSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: {
          employees: action.userGroup.employees,
        },
        id: action.userGroup.id,
      },
      state,
    ),
  })),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getUserGroupError = (state: State) => state.error;
export const getUserGroupLoading = (state: State) => state.loading;
export const getUserGroupSearchText = (state: State) => state.searchText;
export const getSortBy = (state: State) => state.sortBy;
export const getSortByList = (state: State) => state.sortByList;
